import React, { useContext, useEffect, useState, useRef } from "react";
import axios, { AxiosResponse } from "axios";
import { UserProps } from "../types/auth";
import { api } from "../api/base";
import { useLocation } from "react-router-dom";

export interface UserContextActions {
  plan: {
    planId: string;
    planName: string;
  };
  user: UserProps;
  otp: string;
  isUserLoggedIn: boolean;
  fetchingUserData: boolean;
  isPasswordSet: boolean;
  pricingContext: PricingContext | null;
  setPlan: ({ planId, planName }: { planId: string; planName: string }) => void;
  setUser: (user: UserProps) => void;
  clearUser: () => void;
  getProfile: (forceRefresh?: boolean, skipAutoCreate?: boolean) => Promise<void>;
  fetchOtp: () => Promise<void>;
  setOtp: (otp: string) => void;
  setIsPasswordSet: (isPasswordSet: boolean) => void;
  fetchPricingContext: () => Promise<PricingContext | null>;
}

export interface PricingContext {
  plan: string;
  hasActiveSubscription: boolean;
  canUpgrade: boolean;
  canDowngrade: boolean;
  hasMultipleSubscriptions: boolean;
  isTrialing: boolean;
  trialEndsAt: string | null;
}

interface UserContextProviderActions {
  children: React.ReactNode;
}

const UserContext = React.createContext({} as UserContextActions);

export const UserProvider: React.FC<UserContextProviderActions> = ({
  children,
}: any) => {
  const [plan, setPlan] = useState<{ planId: string; planName: string }>({
    planId: "",
    planName: "",
  });
  const [userProps, setUserProps] = useState<UserProps>({} as UserProps);
  const [otp, setOtp] = useState<string>("");
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(false);
  const [isPasswordSet, setIsPasswordSet] = useState<boolean>(false);
  const [fetchingUserData, setFetchingUserData] = useState<boolean>(true);
  const [hasInitiallyFetched, setHasInitiallyFetched] = useState<boolean>(false);
  const [pricingContext, setPricingContext] = useState<PricingContext | null>(null);
  const location = useLocation();
  const previousLocation = useRef(location.pathname);

  // List of public paths that don't require authentication
  const publicPaths = ["/", "/hardware", "/rec-setup", "/pricing", "/arrange", "/waitlist", "/login"];
  
  const isPublicPath = (path: string) => {
    // Check if it's a public path or onboarding, but exclude /account paths
    if (path.startsWith("/account")) {
      return false;
    }
    return publicPaths.includes(path) || path.startsWith("/onboarding");
  };

  const fetchOtp = async () => {
    try {
      const response = await api.get("/auth/otp");
      setOtp(response.data.otp);
    } catch (error) {
      console.error("Error fetching OTP:", error);
    }
  };

  const clearUser = () => {
    setIsUserLoggedIn(false);
    setIsPasswordSet(false);
    setUserProps({} as UserProps);
  };

  const getProfile = async (forceRefresh: boolean = false, skipAutoCreate: boolean = false) => {
    // Don't fetch profile if we're on a public path and don't have a token
    const token = localStorage.getItem("access_token");
    if (!token && isPublicPath(location.pathname)) {
      setFetchingUserData(false);
      setHasInitiallyFetched(true);
      return;
    }

    // If we already have user data and a valid token, don't re-fetch unless forced
    // This prevents unnecessary fetches when navigating to public pages like /pricing
    if (!forceRefresh && hasInitiallyFetched && userProps?.email && token) {
      // Just ensure the state is consistent
      setIsUserLoggedIn(true);
      setFetchingUserData(false);
      return;
    }

    setFetchingUserData(true);
    
    const tryGetProfileUser = async () => {
      if (!token) {
        return null;
      }

      try {
        const url = skipAutoCreate ? "auth/profile?skipAutoCreate=true" : "auth/profile";
        const { data }: AxiosResponse<any, any> = await api.get(url);
        return data?.user;
      } catch (err) {
        console.log("Failed to get profile: ", err);
        // If we get a 401, clear the invalid token
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          // Check if this is a page refresh by looking at navigation type
          const isPageRefresh = window.performance &&
            window.performance.navigation &&
            window.performance.navigation.type === 1;
          
          // Only clear tokens if this is NOT a page refresh
          // This prevents logout on refresh due to timing issues
          if (!isPageRefresh) {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            // Clear user state on 401
            setIsUserLoggedIn(false);
            setIsPasswordSet(false);
            setUserProps({} as UserProps);
          }
        }
        return null;
      }
    };

    try {
      const user = await tryGetProfileUser();

      // User is logged in if we successfully retrieved their profile
      const isLoggedIn = !!user;
      const passwordIsSet = user && user.password !== "";
      
      setIsUserLoggedIn(isLoggedIn);
      setIsPasswordSet(passwordIsSet);

      if (user) {
        localStorage.setItem("user_email", user.email);
        setUserProps(user);
      } else if (!token) {
        // Clear user state if no token
        setUserProps({} as UserProps);
      }
    } finally {
      setFetchingUserData(false);
      setHasInitiallyFetched(true);
    }
  };

  const fetchPricingContext = async (): Promise<PricingContext | null> => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return null;
    }

    try {
      const { data } = await api.get<PricingContext>("auth/pricing-context");
      setPricingContext(data);
      return data;
    } catch (error: any) {
      console.error("Failed to fetch pricing context:", error);
      if (axios.isAxiosError(error)) {
        console.error("Pricing context API error response:", error.response?.data);
      }
      // Return default context on error
      const defaultContext: PricingContext = {
        plan: 'free',
        hasActiveSubscription: false,
        canUpgrade: true,
        canDowngrade: false,
        hasMultipleSubscriptions: false,
        isTrialing: false,
        trialEndsAt: null
      };
      setPricingContext(defaultContext);
      return defaultContext;
    }
  };

  useEffect(() => {
    // Only fetch profile on initial mount
    if (!hasInitiallyFetched) {
      getProfile();
      return;
    }

    // Skip fetching if we're navigating between public paths
    // EXCEPT for pricing page which needs fresh user data for Pro discount
    const needsFreshData = location.pathname === '/pricing' || location.pathname === '/hardware';
    
    if (isPublicPath(previousLocation.current) && isPublicPath(location.pathname) && !needsFreshData) {
      previousLocation.current = location.pathname;
      return;
    }
    
    // Force refresh on pricing/hardware pages if we have a token
    if (needsFreshData && localStorage.getItem("access_token")) {
      getProfile(true);
    }

    // Only fetch profile when navigating to/from protected routes or when we have a token
    const hasToken = localStorage.getItem("access_token");
    if (hasToken || !isPublicPath(location.pathname)) {
      getProfile();
    }
    
    previousLocation.current = location.pathname;
  }, [location.pathname]);

  return (
    <UserContext.Provider
      value={{
        setPlan,
        plan,
        user: userProps,
        setUser: setUserProps,
        otp,
        fetchOtp,
        setOtp,
        clearUser,
        getProfile,
        isUserLoggedIn,
        fetchingUserData,
        isPasswordSet,
        setIsPasswordSet,
        pricingContext,
        fetchPricingContext,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextActions => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUserContext must be used within an UserProvider");
  }

  return context;
};
